import get from "lodash/get";
import React, { useMemo, useState } from "react";
import { DownloadIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import UserProfileModal from "~/pages/BaseStaff/EmployeeProfileViewing/EmployeeProfileView/components/UserProfileModal";
import Status from "~/pages/BaseStaff/ViewingBaseStaff/ui/Status";
import { getFormatDate, getShortDate } from "~/libs/getFormatDate";
import {
  getTrainingLicenseDisplayState,
  getTrainingLicenseLabel,
} from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/trainingLicense";
import {
  DataRow,
  DataRows,
} from "~/pages/BaseStaff/EmployeeProfileViewing/EmployeeProfileView/components/DataRows";
import { formatDisplayValue } from "~/pages/BaseStaff/EmployeeProfileViewing/EmployeeProfileView/model/display";
import {
  TEmployeePosition,
  TEmployeePositionDealerWithMeta,
} from "~/pages/BaseStaff/EmployeeProfileViewing/EmployeeProfileView/model/types";
import { StatusSelectOptions } from "~/pages/BaseStaff/options";
import { dealerAndHoldingRoleChecker } from "~/pages/BaseStaff/ViewingBaseStaff/config/dealerAndHoldingRoleChecker";
import { encodeDealers } from "~/pages/BaseStaff/ViewingBaseStaff/config/encodeDealers";
import { openStaffEmployeeWindow } from "~/shared/lib/createURL";

import s from "~/pages/BaseStaff/EmployeeProfileViewing/EmployeeProfileView/EmployeeProfileView.less";

interface IPositionViewItemProps {
  position: TEmployeePosition;
  employeeId: string;
  refetch: () => void;
}

const PositionViewItem = ({
  position,
  employeeId,
  refetch,
}: IPositionViewItemProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedUser, setSelectedUser] = useState<{
    user: TEmployeePositionDealerWithMeta["hireByUser"];
    titleKey: string;
  } | null>(null);

  const { number, ActivePosition } = position;
  const isMain = number === "one";
  const sectionTitle = isMain
    ? t("employee.positionMainTitle")
    : t("employee.positionSecondaryTitle");
  const blockClassName = isMain
    ? s.positionBlockMain
    : s.positionBlockSecondary;

  const positionData = ActivePosition?.Position;
  const education = ActivePosition?.Education;
  const dealers = useMemo(() => {
    const all = ActivePosition?.Positions || [];
    const seen = new Set<string>();
    const unique: typeof all = [];
    for (const dealer of all) {
      if (!dealer) continue;
      const key = String(
        dealer.DealerId || dealer.InstanceId || dealer.id || "",
      );
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(dealer);
    }
    return unique;
  }, [ActivePosition]);

  const positionName = positionData?.name || "";
  const positionId = positionData?.id != null ? String(positionData.id) : "";
  const positionIds = positionId ? [Number(positionId)] : [];
  const dealersIds = useMemo(() => encodeDealers(dealers as never), [dealers]);

  const licenseFreeCount = Number(
    get(ActivePosition, "licenseFreeCount", 0) || 0,
  );
  const licenseAssigned = Boolean(positionId && licenseFreeCount > 0);

  const licenseText = useMemo(() => {
    const displayState = getTrainingLicenseDisplayState({
      positionId,
      educationTraining: education?.training || undefined,
      licenseAssigned,
      licenseFreeCount,
    });

    return getTrainingLicenseLabel(t, displayState, licenseFreeCount);
  }, [education?.training, licenseAssigned, licenseFreeCount, positionId, t]);

  const openTrainingLink = () => {
    const filterItems = dealersIds.map((item) => {
      const [id, encodedName] = item.split(",");
      return {
        value: id,
        label: decodeURIComponent(encodedName).trim(),
      };
    });

    openStaffEmployeeWindow({
      position: positionName,
      positionIds,
      filterBy: "DEALER",
      period: getShortDate(new Date()),
      filterItems,
      employeeIds: employeeId,
    });
  };

  const openProfileUser = (
    user: TEmployeePositionDealerWithMeta["hireByUser"] | null | undefined,
    titleKey: string,
  ) => {
    if (!user) {
      return;
    }

    if (
      user.role &&
      user.Employee?.id &&
      dealerAndHoldingRoleChecker(user.role)
    ) {
      history.replace(`/base/viewing/${user.Employee.id}`);
      return;
    }

    setSelectedUser({ user, titleKey });
  };

  const closeProfileUser = () => setSelectedUser(null);

  const renderEducationValue = (value?: string) => {
    const text = value ? t(`employee.${value}`) : formatDisplayValue(null);

    if (!value) {
      return text;
    }

    return (
      <button type="button" className={s.linkOrange} onClick={openTrainingLink}>
        {text}
      </button>
    );
  };

  if (!ActivePosition) {
    return (
      <div className={blockClassName}>
        <h3 className={s.positionTitle}>{sectionTitle}</h3>
        <div className={s.positionRows}>
          <DataRows>
            <DataRow label={`${t("employee.namePosition")}:`}>
              {t("employee.does_not_work")}
            </DataRow>
          </DataRows>
        </div>
      </div>
    );
  }

  const renderDealer = (
    dealer: TEmployeePositionDealerWithMeta,
    index: number,
  ) => {
    return (
      <div className={s.positionDealer} key={dealer.id || index}>
        <DataRows>
          <DataRow label={`${t("employee.organizationType")}:`}>
            {dealer.bindingType === "PARTNER"
              ? t("employee.partner")
              : t("employee.dealerCenter")}
          </DataRow>
          <DataRow label={`${t("employee.organization")}:`}>
            {formatDisplayValue(dealer.Dealer?.DealerName)}
          </DataRow>
          <DataRow label={`${t("employee.email")}:`}>
            {formatDisplayValue(dealer.email)}
          </DataRow>
          <DataRow label={`${t("employee.dataConsent")}:`} alignTop>
            {dealer.DataConsent?.link ? (
              <a
                className={s.linkFile}
                href={dealer.DataConsent.link}
                target="_blank"
                rel="noreferrer"
              >
                {dealer.DataConsent.name}
                <DownloadIcon size={12} fill="#495a64" />
              </a>
            ) : (
              formatDisplayValue(null)
            )}
          </DataRow>
          <DataRow label={`${t("employee.jobDescription")}:`} alignTop>
            {dealer.jobDescription?.link ? (
              <a
                className={s.linkFile}
                href={dealer.jobDescription.link}
                target="_blank"
                rel="noreferrer"
              >
                {dealer.jobDescription.name}
                <DownloadIcon size={12} fill="#495a64" />
              </a>
            ) : (
              formatDisplayValue(null)
            )}
          </DataRow>
          <DataRow
            label={`${t("employee.status")}:`}
            valueClassName={s.valueActions}
          >
            <div className={s.viewingStatus}>
              <Status
                refetch={refetch}
                statusValue={StatusSelectOptions.find(
                  (option) => option.value === dealer.status,
                )}
                label={t("employee.status")}
                EmployeeId={employeeId}
                positionId={String(dealer.InstanceId || "")}
                allowedRolesForEditing={positionData?.Direction?.role || []}
                instanceId={
                  positionData?.DirectionId != null
                    ? String(positionData.DirectionId)
                    : undefined
                }
                cardView
                rejectReason={dealer.rejectReason}
              />
            </div>
          </DataRow>
          <DataRow label={`${t("employee.lastStatusUpdateByUser")}:`}>
            {dealer.lastStatusUpdateByUser ? (
              <button
                type="button"
                className={s.linkOrange}
                onClick={() =>
                  openProfileUser(
                    dealer.lastStatusUpdateByUser,
                    "employee.whoLastStatusUpdateByUser",
                  )
                }
              >
                {dealer.lastStatusUpdateByUser.fullname}
              </button>
            ) : (
              formatDisplayValue(null)
            )}
          </DataRow>
          <DataRow label={`${t("employee.lastStatusUpdateAt")}:`}>
            {dealer.lastStatusUpdateAt
              ? getFormatDate(dealer.lastStatusUpdateAt, true)
              : formatDisplayValue(null)}
          </DataRow>
          <DataRow label={`${t("employee.hired")}:`}>
            {dealer.hireByUser ? (
              <button
                type="button"
                className={s.linkOrange}
                onClick={() =>
                  openProfileUser(dealer.hireByUser, "users.user_profile")
                }
              >
                {dealer.hireByUser.fullname}
              </button>
            ) : (
              formatDisplayValue(null)
            )}
          </DataRow>
          <DataRow label={`${t("employee.positionOrder")}:`}>
            {dealer.positionOrder?.link ? (
              <a
                className={s.linkMuted}
                href={dealer.positionOrder.link}
                target="_blank"
                rel="noreferrer"
              >
                {dealer.positionOrder.name}
              </a>
            ) : (
              formatDisplayValue(null)
            )}
          </DataRow>
          <DataRow label={`${t("employee.startDate")}:`}>
            {dealer.startDate
              ? getFormatDate(dealer.startDate, true)
              : formatDisplayValue(null)}
          </DataRow>
          <DataRow label={`${t("employee.startOrderNumber")}:`}>
            {formatDisplayValue(dealer.startOrderNumber)}
          </DataRow>
          {dealer.endDate && (
            <DataRow label={`${t("employee.endDate")}:`}>
              {getFormatDate(dealer.endDate, true)}
            </DataRow>
          )}
          {dealer.endOrderNumber && (
            <DataRow label={`${t("employee.endOrderNumber")}:`}>
              {dealer.endOrderNumber}
            </DataRow>
          )}
        </DataRows>
      </div>
    );
  };

  return (
    <div className={blockClassName}>
      <h3 className={s.positionTitle}>{sectionTitle}</h3>

      <div className={s.positionRows}>
        <DataRows>
          <DataRow label={`${t("employee.namePosition")}:`}>
            {positionData
              ? `${positionData.code}. ${positionData.name}`
              : t("employee.noPosition")}
          </DataRow>
          <DataRow label={`${t("employee.EducationTraining")}:`}>
            {renderEducationValue(education?.training)}
          </DataRow>
          <DataRow label={`${t("employee.EducationCertificate")}:`}>
            {renderEducationValue(education?.certificate)}
          </DataRow>
          {isMain && (
            <DataRow label={`${t("employee.trainingLicenses")}:`}>
              {licenseText}
            </DataRow>
          )}
        </DataRows>

        {dealers.map((dealer, index) => {
          if (!dealer) {
            return null;
          }

          return renderDealer(dealer as TEmployeePositionDealerWithMeta, index);
        })}
      </div>

      <UserProfileModal
        isOpen={Boolean(selectedUser)}
        titleKey={selectedUser?.titleKey}
        user={selectedUser?.user}
        onClose={closeProfileUser}
      />
    </div>
  );
};

export default PositionViewItem;
